/**
 * Guest session + optional wallet slot — the vanilla, framework-agnostic
 * core of the identity module.
 *
 * Storage shape (localStorage, prefix `solblox.`):
 *   solblox.guest    → JSON GuestSession  ({ id, displayName, avatarUrl })
 *   solblox.wallet   → JSON WalletSlot    ({ pubkey, adapter, attachedAt })
 *
 * Module load is intentionally side-effect-free: nothing is read from
 * storage until a function is called. That keeps SSR-safe, makes tests
 * easier, and means the React Provider controls when generation happens.
 */

import type { GuestSession, WalletSlot } from "@solblox/shared";

import { randomDisplayName } from "./displayName.js";
import { readJSON, writeJSON, removeKey } from "./storage.js";

const GUEST_KEY = "guest";
const WALLET_KEY = "wallet";

/** Empty/unattached wallet sentinel. */
export const EMPTY_WALLET: WalletSlot = {
  pubkey: "",
  adapter: "",
  attachedAt: 0,
};

type Listener = (snapshot: { guest: GuestSession; wallet: WalletSlot }) => void;
const listeners: Set<Listener> = new Set();

// In-memory cache so React components reading via getSnapshot stay stable.
let cachedGuest: GuestSession | null = null;
let cachedWallet: WalletSlot | null = null;

function generateId(): string {
  // crypto.randomUUID is available in all modern browsers + node 19+.
  // Fall back to a v4-shaped random string built from Math.random for the
  // unlikely case it is missing (e.g. very old WebViews).
  try {
    const g = globalThis as { crypto?: { randomUUID?: () => string } };
    if (g.crypto && typeof g.crypto.randomUUID === "function") {
      return g.crypto.randomUUID();
    }
  } catch {
    // ignore, fall through
  }
  const hex = (n: number): string => Math.floor(Math.random() * n).toString(16);
  const block = (len: number): string => {
    let out = "";
    for (let i = 0; i < len; i += 1) out += hex(16);
    return out;
  };
  return `${block(8)}-${block(4)}-4${block(3)}-${((Math.floor(Math.random() * 4) + 8) | 0).toString(16)}${block(3)}-${block(12)}`;
}

function isGuestSession(value: unknown): value is GuestSession {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === "string" &&
    typeof v.displayName === "string" &&
    typeof v.avatarUrl === "string"
  );
}

function isWalletSlot(value: unknown): value is WalletSlot {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.pubkey === "string" &&
    typeof v.adapter === "string" &&
    typeof v.attachedAt === "number"
  );
}

function loadWallet(): WalletSlot {
  if (cachedWallet) return cachedWallet;
  const raw = readJSON<unknown>(WALLET_KEY);
  if (isWalletSlot(raw)) {
    cachedWallet = raw;
    return raw;
  }
  cachedWallet = EMPTY_WALLET;
  return cachedWallet;
}

function emit(): void {
  const snapshot = {
    guest: cachedGuest ?? getOrCreateGuestSession(),
    wallet: loadWallet(),
  };
  for (const fn of listeners) {
    try {
      fn(snapshot);
    } catch {
      // listener errors must not break others
    }
  }
}

/**
 * Return the persisted guest session, creating + persisting one if absent.
 * Pure function from the caller's perspective; safe to call repeatedly.
 */
export function getOrCreateGuestSession(): GuestSession {
  if (cachedGuest) return cachedGuest;
  const existing = readJSON<unknown>(GUEST_KEY);
  if (isGuestSession(existing)) {
    cachedGuest = existing;
    return existing;
  }
  const created: GuestSession = {
    id: generateId(),
    displayName: randomDisplayName(),
    avatarUrl: "",
  };
  writeJSON(GUEST_KEY, created);
  cachedGuest = created;
  return created;
}

/** Returns the current wallet slot (possibly empty). Does not create one. */
export function getWalletSlot(): WalletSlot {
  return loadWallet();
}

/**
 * Persist a wallet attachment for the current guest. Idempotent: if the
 * pubkey matches the cached slot, no work is done and no listeners fire.
 */
export function attachWallet(pubkey: string, adapter: string = ""): void {
  if (typeof pubkey !== "string" || pubkey.length === 0) return;
  const current = loadWallet();
  if (current.pubkey === pubkey && current.adapter === adapter) return;
  const slot: WalletSlot = {
    pubkey,
    adapter,
    attachedAt: Date.now(),
  };
  writeJSON(WALLET_KEY, slot);
  cachedWallet = slot;
  emit();
}

/** Clear the wallet slot. Idempotent. */
export function detachWallet(): void {
  const current = loadWallet();
  if (current.pubkey === "" && current.adapter === "" && current.attachedAt === 0) {
    return;
  }
  removeKey(WALLET_KEY);
  cachedWallet = EMPTY_WALLET;
  emit();
}

/**
 * Subscribe to identity changes. Returns an unsubscribe function. The
 * listener is NOT called on subscribe — callers should pull initial state
 * via getOrCreateGuestSession / getWalletSlot.
 */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Testing seam: drop the in-memory cache so the next read re-hydrates from
 * storage. Not part of the public surface; safe to keep in production
 * builds (tree-shaken if unused).
 */
export function _resetCacheForTests(): void {
  cachedGuest = null;
  cachedWallet = null;
}
